import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect, useState } from "react";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import { setupSupabaseTable, migrateDataToSupabase } from "./lib/setupSupabase";

const queryClient = new QueryClient();

// Component to handle Supabase setup
const SupabaseSetup = ({ children }: { children: React.ReactNode }) => {
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const setup = async () => {
      try {
        // Setup Supabase table
        const tableCreated = await setupSupabaseTable();
        
        if (tableCreated) {
          // Migrate existing data from localStorage to Supabase
          await migrateDataToSupabase();
        }
        
        setIsSetupComplete(true);
      } catch (err) {
        console.error('Error during Supabase setup:', err);
        setError('Error setting up Supabase. Check console for details.');
        setIsSetupComplete(true); // Continue anyway to allow app to function
      }
    };

    setup();
  }, []);

  if (!isSetupComplete) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold gold-gradient mb-4">Configurando la aplicación...</h2>
          <div className="w-16 h-16 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error) {
    console.warn('Continuing with application despite Supabase setup error');
  }

  return <>{children}</>;
};

// Component to render the seed HTML page
const SeedPage = () => {
  useEffect(() => {
    // Redirect to the seed.html page
    window.location.href = '/src/scripts/seed.html';
  }, []);

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-xl font-bold gold-gradient mb-4">Redirigiendo a la página de seed...</h2>
        <div className="w-16 h-16 border-4 border-gold border-t-transparent rounded-full animate-spin mx-auto"></div>
      </div>
    </div>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <SupabaseSetup>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/seed" element={<SeedPage />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </SupabaseSetup>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
